'use client';

import html2canvas from 'html2canvas';
import { AlertCircle, Loader2, Maximize2 } from 'lucide-react';
import posthog from 'posthog-js';
import { usePostHog } from 'posthog-js/react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { CopyButtonBase } from '@/components/ui/copy-button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

declare global {
  interface Window {
    mermaid: {
      initialize: (config: unknown) => void;
      render: (id: string, chart: string) => Promise<{ svg: string }>;
    };
    __mermaidLoaded?: boolean;
    __mermaidCallbacks?: Array<() => void>;
  }
}

const MERMAID_THEME = {
  startOnLoad: false,
  securityLevel: 'loose',
  theme: 'base',
  themeVariables: {
    primaryColor: '#f5f5f5',
    primaryTextColor: '#171717',
    primaryBorderColor: '#d4d4d4',
    lineColor: '#a3a3a3',
    secondaryColor: '#fafafa',
    tertiaryColor: '#f5f5f5',
    background: '#ffffff',
    mainBkg: '#f5f5f5',
    nodeBorder: '#d4d4d4',
    clusterBkg: '#fafafa',
    titleColor: '#171717',
    edgeLabelBackground: '#ffffff',
    actorBkg: '#f5f5f5',
    actorBorder: '#d4d4d4',
    actorTextColor: '#171717',
    actorLineColor: '#a3a3a3',
    signalColor: '#171717',
    signalTextColor: '#171717',
    labelBoxBkgColor: '#f5f5f5',
    labelBoxBorderColor: '#d4d4d4',
    labelTextColor: '#171717',
    loopTextColor: '#171717',
    noteBorderColor: '#d4d4d4',
    noteBkgColor: '#fafafa',
    noteTextColor: '#171717',
    activationBorderColor: '#d4d4d4',
    activationBkgColor: '#fafafa',
    sequenceNumberColor: '#171717',
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  },
  flowchart: {
    curve: 'basis',
    padding: 15,
    nodeSpacing: 50,
    rankSpacing: 50,
  },
};

/**
 * Helper to safely format a label text by sanitizing quotes and newlines.
 */
function formatMermaidLabel(text: string): string {
  let clean = text.trim();
  if (clean.startsWith('"') && clean.endsWith('"') && clean.length >= 2) {
    clean = clean.substring(1, clean.length - 1);
  } else if (clean.startsWith('"')) {
    clean = clean.substring(1);
  } else if (clean.endsWith('"')) {
    clean = clean.substring(0, clean.length - 1);
  }

  clean = clean.replace(/"/g, "'");
  // Escape Mermaid-reserved chars inside labels: a raw | ends an edge |label|
  // early (the reported PIPE parse error), and # starts an entity. Existing
  // entities (#124;) are left intact. <br/> is injected after, so it survives.
  clean = clean.replace(/#(?!\d+;)/g, '#35;');
  clean = clean.replace(/\|/g, '#124;');
  clean = clean.replace(/\n/g, '<br/>');
  return `"${clean}"`;
}

/**
 * Helper to parse and extract the label text based on Mermaid's quoting rules.
 *
 * The scan is quote-aware: the label ends at the first delimiter that appears
 * outside a "..." section, mirroring Mermaid's own grammar. A naive
 * first-delimiter search breaks on labels like -->|note: "a | b"|, where
 * the pipe inside quotes would end the label early (PIPE parse error).
 */
function parseMermaidLabel(
  chart: string,
  startIndex: number,
  endDelimiter: string
): { labelText: string; endIndex: number; foundDelimiter: boolean } {
  const i = startIndex;
  let inQuotes = false;
  let j = i;
  while (j < chart.length) {
    const ch = chart[j];
    if (ch === '\n') break;
    if (ch === '\\') {
      j += 2;
      continue;
    }
    if (ch === '"') {
      inQuotes = !inQuotes;
      j++;
      continue;
    }
    if (!inQuotes && chart.startsWith(endDelimiter, j)) {
      return { labelText: chart.substring(i, j), endIndex: j, foundDelimiter: true };
    }
    j++;
  }

  // Fallback (unbalanced quotes): find the first delimiter on the current line
  const delimIdx = chart.indexOf(endDelimiter, i);
  const newlineIdx = chart.indexOf('\n', i);

  if (delimIdx !== -1 && (newlineIdx === -1 || delimIdx < newlineIdx)) {
    return { labelText: chart.substring(i, delimIdx), endIndex: delimIdx, foundDelimiter: true };
  }

  // No delimiter found on this line
  const end = newlineIdx !== -1 ? newlineIdx : chart.length;
  return { labelText: chart.substring(i, end), endIndex: end, foundDelimiter: false };
}

/**
 * Helper to parse and sanitize a mermaid node definition starting at index `start`.
 * Returns the sanitized string chunk and the new index.
 * If no node is matched, returns null.
 */
function parseMermaidNode(
  chart: string,
  start: number
): { text: string; nextIndex: number } | null {
  const nodeMatch = chart.substring(start).match(/^([a-zA-Z0-9_-]+)(\(\(|\[\[|\{\{|\[\(|\(|\[|\{)/);
  if (!nodeMatch) return null;

  const nodeId = nodeMatch[1];
  const shapeOpen = nodeMatch[2];

  let shapeClose = '';
  if (shapeOpen === '((') shapeClose = '))';
  else if (shapeOpen === '[[') shapeClose = ']]';
  else if (shapeOpen === '{{') shapeClose = '}}';
  else if (shapeOpen === '[(') shapeClose = ')]';
  else if (shapeOpen === '(') shapeClose = ')';
  else if (shapeOpen === '[') shapeClose = ']';
  else if (shapeOpen === '{') shapeClose = '}';

  let result = nodeId + shapeOpen;
  let i = start + nodeMatch[0].length;

  const parsed = parseMermaidLabel(chart, i, shapeClose);
  result += formatMermaidLabel(parsed.labelText);
  result += shapeClose;

  i = parsed.endIndex;
  if (parsed.foundDelimiter) {
    i += shapeClose.length;
    // Eat any trailing hallucinated characters
    while (i < chart.length) {
      const char = chart[i];
      if (
        char === ' ' ||
        char === '\n' ||
        char === '\r' ||
        chart.substring(i).match(/^(?:-{2,}>?|={2,}>?|<--+>?|o--+o|x--+x|-\.-+>?)/)
      ) {
        break;
      }
      if (/[)\]}"']/.test(char)) {
        i++;
      } else {
        break;
      }
    }
  }

  return { text: result, nextIndex: i };
}

/**
 * Sanitizes mermaid chart code to be fault-tolerant using a robust lexer.
 * It gracefully handles:
 * 1. Internal quotes (converting to single quotes)
 * 2. Unbalanced brackets/parentheses inside labels
 * 3. Literal newlines (converting to <br/>)
 * 4. Trailing hallucinated delimiters (e.g. A("(Mulai")) -> A("Mulai"))
 */
export function sanitizeMermaidChart(chart: string): string {
  // The model often chains a text edge into a labeled edge:
  //   A -- Belum Lengkap -->|"P-18 ..."| B
  // which is invalid Mermaid (after `-- text -->` a node must follow, not
  // `|label|`). Merge the two labels into one pipe edge; the lexer below
  // then quotes/escapes it like any other edge label.
  const normalized = chart.replace(/--\s*([^-|\n][^|\n]*?)\s*-->\|/g, (_, text: string) => `-->|${text.trim()}: `);

  let result = '';
  let i = 0;

  while (i < normalized.length) {
    // 1. Check for Edge label: -->|label|
    const edgeMatch = normalized
      .substring(i)
      .match(/^((?:-{2,}>?|={2,}>?|<--+>?|o--+o|x--+x|-\.-+>?))\|/);
    if (edgeMatch) {
      const arrow = edgeMatch[1];
      result += arrow + '|';
      i += edgeMatch[0].length;

      const parsed = parseMermaidLabel(normalized, i, '|');
      result += formatMermaidLabel(parsed.labelText);

      i = parsed.endIndex;
      if (parsed.foundDelimiter) {
        result += '|';
        i++;
      }
      continue;
    }

    // 2. Check for Node definitions
    const nodeResult = parseMermaidNode(normalized, i);
    if (nodeResult) {
      result += nodeResult.text;
      i = nodeResult.nextIndex;
      continue;
    }

    result += normalized[i];
    i++;
  }

  return result;
}

function loadMermaid(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Not in browser'));
  }

  if (window.__mermaidLoaded && window.mermaid) {
    return Promise.resolve();
  }

  if (window.__mermaidCallbacks) {
    return new Promise((resolve) => {
      window.__mermaidCallbacks!.push(resolve);
    });
  }

  window.__mermaidCallbacks = [];

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js';
    script.async = true;
    script.onload = () => {
      window.__mermaidLoaded = true;
      window.mermaid.initialize(MERMAID_THEME);
      window.__mermaidCallbacks?.forEach((cb) => cb());
      window.__mermaidCallbacks = [];
      resolve();
    };
    script.onerror = () => {
      const error = new Error('Failed to load mermaid');
      posthog?.captureException(error, { operation: 'mermaid_script_load' });
      reject(error);
    };
    document.head.appendChild(script);
  });
}

export async function renderMermaidChart(chart: string): Promise<string> {
  await loadMermaid();

  const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const { svg } = await window.mermaid.render(id, chart);
  return svg;
}

/**
 * Converts a Mermaid chart definition into a high-resolution PNG base64 Data URL.
 * Used to insert diagrams into Microsoft Word documents or rich text destinations.
 */
export async function mermaidToPngBase64(chart: string): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  try {
    const sanitized = sanitizeMermaidChart(chart.trim());
    if (!sanitized || sanitized.length < 5) return null;

    const svg = await renderMermaidChart(sanitized);

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-99999px';
    container.style.top = '0';
    container.style.background = '#ffffff';
    container.style.padding = '16px';
    container.innerHTML = svg;
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
      });

      return canvas.toDataURL('image/png');
    } finally {
      document.body.removeChild(container);
    }
  } catch (err) {
    console.warn('[mermaidToPngBase64] Failed to convert mermaid to image:', err);
    return null;
  }
}

interface MermaidProps {
  chart: string;
  className?: string;
  onChartClick?: (chart: string) => void;
  enableCopy?: boolean;
  isStreaming?: boolean;
}

export function Mermaid({
  chart,
  className,
  onChartClick,
  enableCopy = true,
  isStreaming = false,
}: MermaidProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const lastRenderedChartRef = useRef<string | null>(null);
  const renderInProgressRef = useRef(false);
  const [hasBeenStreaming, setHasBeenStreaming] = useState(false);
  const posthog = usePostHog();

  const sanitizedChart = sanitizeMermaidChart(chart.trim());

  useEffect(() => {
    if (isStreaming) {
      setHasBeenStreaming(true);
    }
  }, [isStreaming]);

  useEffect(() => {
    if (!sanitizedChart || sanitizedChart.length < 10) {
      return;
    }

    if (sanitizedChart === lastRenderedChartRef.current && isRendered) {
      return;
    }

    if (isStreaming) {
      return;
    }

    if (renderInProgressRef.current) {
      return;
    }

    let cancelled = false;
    renderInProgressRef.current = true;

    const renderChart = async () => {
      try {
        const svg = await renderMermaidChart(sanitizedChart);

        if (cancelled || !containerRef.current) {
          return;
        }

        containerRef.current.innerHTML = svg;
        lastRenderedChartRef.current = sanitizedChart;
        setHasBeenStreaming(false);
        setIsRendered(true);
        setError(null);
      } catch (err) {
        if (cancelled) return;

        const errorMessage = err instanceof Error ? err.message : 'Gagal merender diagram';
        posthog?.captureException(err, {
          operation: 'mermaid_render',
          chartPreview: sanitizedChart.slice(0, 100),
        });
        setError(errorMessage);
        setIsRendered(false);
      } finally {
        renderInProgressRef.current = false;
      }
    };

    setError(null);
    renderChart();

    return () => {
      cancelled = true;
      renderInProgressRef.current = false;
    };
  }, [sanitizedChart, isStreaming, isRendered, posthog]);

  const handleCopyAsImage = useCallback(async () => {
    if (!containerRef.current || isCopying) return;

    setIsCopying(true);
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (!blob) {
        throw new Error('Failed to create blob');
      }

      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy image:', error);
      posthog?.captureException(error, { operation: 'mermaid_copy_image' });
    } finally {
      setIsCopying(false);
    }
  }, [isCopying, posthog]);

  const handleClick = useCallback(() => {
    if (onChartClick && !error) {
      onChartClick(chart);
    }
  }, [onChartClick, chart, error]);

  const showLoading = isStreaming || (hasBeenStreaming && !isRendered && !error);
  const showError = error && !isStreaming;

  if (showError) {
    return (
      <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg my-4">
        <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-red-800">Gagal merender diagram</p>
          <p className="text-xs text-red-600 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative my-4">
      {showLoading && (
        <div className="flex items-center justify-center p-6 bg-neutral-50 border border-dashed border-neutral-200 rounded-lg">
          <span className="text-sm text-neutral-500">Diagram akan muncul di sini...</span>
        </div>
      )}
      <div
        ref={containerRef}
        onClick={handleClick}
        className={cn(
          'overflow-x-auto [&_svg]:max-w-full [&_svg]:mx-auto [&_.label]:font-inter [&_.nodeLabel]:font-inter [&_.cluster-label]:font-inter',
          onChartClick && 'cursor-pointer hover:bg-neutral-50 rounded-lg transition-colors',
          showLoading && 'hidden',
          className
        )}
      />
      {isRendered && enableCopy && (
        <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onChartClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 backdrop-blur-sm border border-neutral-200 hover:border-neutral-300 text-[10px] font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
              title="Perbesar"
            >
              <Maximize2 size={12} />
            </button>
          )}
          <span onClick={(e) => e.stopPropagation()}>
            <CopyButtonBase
              onCopy={handleCopyAsImage}
              isCopying={isCopying}
              copied={copied}
              variant="floating"
              title={copied ? 'Disalin' : 'Salin sebagai gambar'}
            />
          </span>
        </div>
      )}
    </div>
  );
}

interface MermaidModalProps {
  chart: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MermaidModal({ chart, open, onOpenChange }: MermaidModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const posthog = usePostHog();

  const sanitizedChart = sanitizeMermaidChart(chart.trim());

  useEffect(() => {
    if (!open || !sanitizedChart) return;

    let cancelled = false;

    const renderChart = async () => {
      try {
        const renderedSvg = await renderMermaidChart(sanitizedChart);
        if (!cancelled) {
          setSvg(renderedSvg);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const errorMessage = err instanceof Error ? err.message : 'Gagal merender diagram';
          posthog?.captureException(err, {
            operation: 'mermaid_modal_render',
            chartPreview: sanitizedChart.slice(0, 100),
          });
          setError(errorMessage);
        }
      }
    };

    renderChart();

    return () => {
      cancelled = true;
    };
  }, [open, sanitizedChart, posthog]);

  const handleCopyAsImage = useCallback(async () => {
    if (!containerRef.current || isCopying) return;

    setIsCopying(true);
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (!blob) {
        throw new Error('Failed to create blob');
      }

      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy image:', error);
      posthog?.captureException(error, { operation: 'mermaid_modal_copy_image' });
    } finally {
      setIsCopying(false);
    }
  }, [isCopying, posthog]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-sm font-medium text-neutral-700">Diagram</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto flex flex-col">
          {error ? (
            <div className="flex items-center justify-center h-64 text-red-500">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">{error}</p>
              </div>
            </div>
          ) : !svg ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
            </div>
          ) : (
            <div
              ref={containerRef}
              className="flex items-center justify-center p-4"
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          )}
        </div>
        <div className="shrink-0 pt-4 border-t border-neutral-100 flex justify-end">
          <CopyButtonBase
            onCopy={handleCopyAsImage}
            isCopying={isCopying}
            copied={copied}
            disabled={isCopying || !svg}
            size="md"
            label="Salin sebagai Gambar"
            copiedLabel="Disalin!"
            className="px-3 py-2 text-xs"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
